'use client';

import { NextIntlClientProvider } from 'next-intl';
import { type ReactNode, useEffect, useState } from 'react';

/**
 * Default timezone fallback for SSR.
 * Client will detect actual timezone via Intl API.
 */
const defaultTimeZone = 'America/New_York';

type Props = {
  locale: string;
  messages: Record<string, any>;
  timeZone?: string;
  now?: Date;
  children: ReactNode;
};

/**
 * Wrapper for NextIntlClientProvider with client-side timezone detection
 *
 * The locale is determined by the route parameter (e.g., /en/, /pt/, /es/)
 * and passed down from the server. This component only handles timezone
 * detection on the client side.
 */
export function SafeI18nProvider({
  locale,
  messages,
  timeZone = defaultTimeZone,
  now,
  children,
}: Props) {
  const [clientTimeZone, setClientTimeZone] = useState<string>(timeZone);

  useEffect(() => {
    // Detect user's timezone client-side
    const detectedTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (detectedTimeZone) {
      setClientTimeZone(detectedTimeZone);
    }
  }, []);

  // If no messages loaded, just render children without i18n
  if (!messages || Object.keys(messages).length === 0) {
    return <>{children}</>;
  }

  return (
    <NextIntlClientProvider
      locale={locale}
      messages={messages}
      timeZone={clientTimeZone}
      now={now}
    >
      {children}
    </NextIntlClientProvider>
  );
}
